import { useNavigate } from "react-router-dom";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import logo from "../assets/images/logo.png";
import { getProfileImage } from "../core/api";
import AppImage from "./AppImage";
import Swal from "sweetalert2";

type HeaderProps = {
  onMenuClick: () => void;
};

const getGreeting = (): string => {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
};

const readProfile = (): any | null => {
  try {
    const raw = localStorage.getItem("adminProfile");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const Header = ({ onMenuClick }: HeaderProps) => {
  const [profileData, setProfileData] = useState<any>(readProfile);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [search, setSearch] = useState("");
  const [greeting, setGreeting] = useState(getGreeting);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { logout } = useAuth();
  const profileImage = getProfileImage(profileData);

  // Sync profile when DashboardLayout refreshes it from the API
  useEffect(() => {
    const handleProfileUpdate = () => setProfileData(readProfile());
    window.addEventListener("adminProfile:updated", handleProfileUpdate);
    return () => window.removeEventListener("adminProfile:updated", handleProfileUpdate);
  }, []);

  // Update greeting every minute so it stays accurate
  useEffect(() => {
    const id = setInterval(() => setGreeting(getGreeting()), 60_000);
    return () => clearInterval(id);
  }, []);

  // Close profile dropdown on outside click
  useEffect(() => {
    if (!showProfileMenu) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(event.target as Node)
      ) {
        setShowProfileMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showProfileMenu]);

  const handleLogout = useCallback(async () => {
    const confirmation = await Swal.fire({
      title: "Log out?",
      text: "You will need to sign in again to access the admin panel.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Log out",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#588742",
    });

    if (!confirmation.isConfirmed) return;

    setShowProfileMenu(false);
    await logout();
    navigate("/");
  }, [logout, navigate]);

  const handleSearch = (event: React.FormEvent) => {
    event.preventDefault();
    const query = search.trim().toLowerCase();
    if (!query) return;

    if (query.includes("order") || query.startsWith("#")) {
      navigate("/orders");
    } else if (query.includes("customer") || query.includes("user")) {
      navigate("/users");
    } else if (query.includes("coupon") || query.includes("discount")) {
      navigate("/coupons");
    } else {
      navigate("/products");
    }
  };

  return (
    <header className="app-header">
      <div className="header-left">
        <button
          type="button"
          onClick={onMenuClick}
          className="menu-button"
          aria-label="Toggle sidebar"
        >
          ☰
        </button>
        <span className="mobile-header-logo">
          <img src={logo} alt="Sugam Store" />
        </span>

        <div className="topbar-welcome">
          <span className="topbar-greeting">{greeting},</span>
          <span className="topbar-name">{profileData?.name}</span>
        </div>
      </div>

      <form className="global-search" onSubmit={handleSearch}>
        <span>🔎</span>
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search for orders, products, user..."
        />
      </form>

      <div className="header-actions">
        <div className="profile-menu-wrap" ref={profileMenuRef}>
          <button
            type="button"
            onClick={() => setShowProfileMenu((current) => !current)}
            className="profile-chip"
            aria-expanded={showProfileMenu}
            aria-haspopup="menu"
          >
            <AppImage
              src={profileImage}
              alt={profileData?.name || "Admin"}
              crossOrigin="anonymous"
            />
            <div>
              <strong>{profileData?.name || "Admin"}</strong>
              <small>{profileData?.role || "Admin"}</small>
            </div>
            <i>⌄</i>
          </button>

          {showProfileMenu && (
            <div className="profile-dropdown" role="menu">
              <button
                type="button"
                onClick={() => {
                  setShowProfileMenu(false);
                  navigate("/profile");
                }}
                role="menuitem"
              >
                <span>👤</span>
                Profile
              </button>
              <button type="button" onClick={handleLogout} role="menuitem">
                <span>🚪</span>
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
